

import { CcfTranslationKey } from '@nice-ccf/i18n';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import { columns } from './ccf-interaction-search-cases-tab/ccf-interaction-search-cases-tab/ccf-interaction-search-cases-tab';

export enum INTERACTION_SEARCH_FILTERS {
  CHANNEL = 'channel',
  STATUS = 'status',
  // = 'skillId',
  // = 'inboxAssigneeAgentId',
  //OWNER_ASSIGNEE = 'ownerAssigneeAgentId',
  FROM = 'from',
  TO = 'to',
  TAG = 'tag',
}

/**
 * Enum for search option
 */
export enum SortingType{
  ASCENDING='asc',
  DESCENDING='dec'
}
/**
 * Enum for search option
 */
export enum SEARCH_OPTIONS{
  CONTENT='content',
  CASE_ID='caseID'
}

export const SEARCH_OPTION_LIST = [SEARCH_OPTIONS.CONTENT, SEARCH_OPTIONS.CASE_ID];

/**
 * Interface for the grid column definition
 */
export interface ColumnDefinitions {
  /**
   * Field will correspond with object in a row.
   * */
  field: string;
  /**
   * headerName will be the name at the top of the column and doesn't
   * correspond to any values.
   *
   */
  headerName: string;
  /**
   * Pixel width of the column
   */
  width?: number;
  /**
   * Can the user edit the value in this column?
   */
  editable?: boolean;
  /**
   * Can pass type for typescript support throughout IDE.
   */
  type?: string;
  /**
   * If `true`, the column is sortable.
   */
  sortable?: boolean;
  /**
   * min width for the column width
   */
  minWidth?: number;
  /**
   * If set, it indicates that a column has fluid width. Range [0, ∞).
   */
  flex?: number;
  /**
   * Function that allows to get a specific data instead of field to render in the cell.
   */
  valueGetter?: (params: any) => string;
  /**
   * Function that allows to apply a formatter before rendering its value.
   */
  valueFormatter?: (params: any) => string;
  /**
   * The order of the sorting sequence.
   */
  sortingOrder?: any;
  /**
   * To render menu options on each grip.
   */
  renderCell? : (params:any)=>any,
  /**
   * Property to hide/show the column
   */
  hide?: boolean;
}

/**
 * Type for filter values fields
 */
export type filterValues  = {
  [key in INTERACTION_SEARCH_FILTERS]: string | any[]
}

export const CCF_INTERACTION_SEARCH_FEATURE_KEY = 'ccfInteractionSearch';

/**
 * Interface for the sorting column object
 */
export interface CcfSortColumn {
  /**
   * current sorting column
   */
  sorting: string;
  /**
   * sorting order for the sorted column- 'asc'/'desc'
   */
  sortingType: SortingType;
}

/**
 * Interface for the initial value of the Interaction search state
 */
export interface CcfInteractionSearchState {
  /**
   * columns which the grid will render
   */
  defaultColumns: ColumnDefinitions[];
  /**
   * grid row data array which grid will display as rows
   */
  gridData: any[];
  /**
   * total records for the current search result
   */
  totalRecords: number;
  /**
   * the scroll token value which will be used to get the next paginated result
   */
  lastScrollToken: string;
  /**
   * boolean flag to know when the grid data is still fetched
   */
  isLoading: boolean;
  /**
   * Value for the current sort column
   */
  currentSortColumn: CcfSortColumn;
   /**
   * holds the target element on whose click menu will open
   */
   customizeMenuElement: null | HTMLElement;
   /**
   * holds the target element on whose click menu will open
   */
   filtersMenuElement: null | HTMLElement;  
   /**
    * number of columns that will be visible depending on screen size
    */
   numberOfVisibleColumns: number;
  /**
   * Selected row contact ids of interaction search grid
  */
  selectedRowContacts: any;
  /**
   * Interaction input search text
   */
  searchText: string;
  /**
  * initial filter values
  */
  defaultFilterValues: filterValues;
  /**
   * all channels list
   */
  allChannelList: any[];
  /**
   * will have the toast message details for interaction search
   */
  interactionSearchToastObj?: InteractionSearchToastObj;
  /**
   * to know if the grid needs to be reset to page one
   */
  isGridReset: boolean;
  /**
   * customer id, case id of selected record and flag to set when click on contact info from search grid kebab menu
   */
  currentCustomerContactInfo: {
    customerId: string;
    caseId: string;
    isCustomerCardPopupOpen: boolean;
  };
  /**
   * boolean flag to know weather the filter is selected or not
   */
  isFilterSelected: boolean;
   /**
   * Interaction search options
   */
   searchOption: SEARCH_OPTIONS;
}

/**
 * Interface for the localStorage values for 
 */
export interface CcfInteractionSearchLS {
  /**
   * columns field and hide value array to be stored in localStorage
   */
  defaultColumns: Array<ColumnDefinitionsInLocalStorage>,
  /**
   * used to save the search input text
   */
  searchText: string,
  /**
   * used to store the current sort columns
   */
  currentSortColumn: CcfSortColumn,
  /**
   * used to store the current filter values
   */
  currentFilterValues: filterValues;
/**
   * boolean flag to know weather the filter is selected or not
   */
  isFilterSelected: boolean;
   /**
   * used to save search option value
   */
   searchOption: SEARCH_OPTIONS,
}

/**
 * Interface for the grid column definition stored in local storage
 */
export interface ColumnDefinitionsInLocalStorage{
  /**
   * field name for the column
   */
  field: string,
  /**
   * property that determines if column is visible or not
   */
  hide?: boolean;
}

/**
 * Interface for Interaction search toast
 */
export interface InteractionSearchToastObj {
  /**
   * if true that means we need to show error toast otherwise success
   */
  isError: boolean;
  /**
   * translation text to show in the toaster
   */
  messageKey: CcfTranslationKey;
  /**
   * placeholder string for message description
  */
  placeHolder?: string;
}


const DEFAULT_SORTING_COLUMN = 'createdAt';
export const INTERACTION_SEARCH = 'search';// localStorage key for the saved interaction search data
export const initialDefaultFilterValues ={
  channel:'',
  status:'',
  // TODO: For now we don't want these filters but in feature will uncomment.
  // inboxAssigneeAgentId:[],
  // ownerAssigneeAgentId:[],
  // skillId:[],
  from:'',
  to:'',
  tag: '',
};


/**
 * Use to get the initial search data
 * if some data is stored in local storage then we will include it as the initial state data also
 * @returns - initial interaction search data
 * @example
 * ```
 * getInitialCcfInteractionSearchStat();
 * ```
 */
export const getInitialCcfInteractionSearchState = (): CcfInteractionSearchState => {
  return {
    defaultColumns: columns as ColumnDefinitions[],
    gridData: [],
    totalRecords: 0,
    lastScrollToken: '',
    isLoading: false,
    currentSortColumn: { sorting: DEFAULT_SORTING_COLUMN, sortingType: SortingType.DESCENDING },
    customizeMenuElement: null,
    numberOfVisibleColumns: 6,
    selectedRowContacts: [],
    searchText: '',
    filtersMenuElement: null,
    defaultFilterValues: initialDefaultFilterValues,
    allChannelList: [],
    isGridReset: false,
    isFilterSelected :  false,
    currentCustomerContactInfo: {
      customerId: '',
      caseId: '',
      isCustomerCardPopupOpen: false,
    },
    searchOption: SEARCH_OPTIONS.CONTENT,
  };
}

export const ccfInteractionSearchSlice = createSlice({
  name: CCF_INTERACTION_SEARCH_FEATURE_KEY,
  initialState: getInitialCcfInteractionSearchState(),
  reducers: {
    updateDefaultColumns: (state, action) => {
      return {
        ...state,
        defaultColumns: action.payload,
      };
    },
    updateGridData: (state, action) => {
      return {
        ...state,
        gridData: action.payload.freshData
          ? [...action.payload.newData]
          : [...state.gridData, ...action.payload.newData],
      };
    },
    updateTotalRecords: (state, action) => {
      return {
        ...state,
        totalRecords: action.payload,
      };
    },
    updateIsLoading: (state, action) => {
      return {
        ...state,
        isLoading: action.payload,
      };
    },
    updateCustomizeMenuElement: (state, action) => {
      return {
        ...state,
        customizeMenuElement: action.payload,
      }
    },
    updateFiltersMenuElement: (state, action) => {
      return {
        ...state,
        filtersMenuElement: action.payload,
      }
    },
    
    resetInteractionState: (state) => {
      return {...getInitialCcfInteractionSearchState()};
    },
    updateSelectedRowContacts: (state, action) => {
      return {
        ...state,
        selectedRowContacts: action.payload,
      }
    },
    clearFilterValues: (state) => {
      return {
        ...state,
        defaultFilterValues: initialDefaultFilterValues,
      }
    },
    setFilterValues: (state, action) => {
      return {
        ...state,
        defaultFilterValues: action.payload,
      }
    },
  },
});

export const ccfInteractionSearchReducer = ccfInteractionSearchSlice.reducer;
export const ccfInteractionSearchActions = ccfInteractionSearchSlice.actions;

/**
 * USe to get the interaction search state
 * @param rootState - interaction state
 * @example
 * ```
 *  createSelector(getCcfInteractionSearchState, (state) => state.defaultColumns)
 * ```
 */
export const getCcfInteractionSearchState = (rootState: {
  [CCF_INTERACTION_SEARCH_FEATURE_KEY]: CcfInteractionSearchState;
}): CcfInteractionSearchState => rootState[CCF_INTERACTION_SEARCH_FEATURE_KEY];

export const getDefaultColumns = createSelector(
  getCcfInteractionSearchState,
  (state) => state.defaultColumns
);

export const getGridData = createSelector(getCcfInteractionSearchState, (state) => state.gridData);

export const getTotalRecords = createSelector(
  getCcfInteractionSearchState,
  (state) => state.totalRecords
);

export const getLastScrollToken = createSelector(
  getCcfInteractionSearchState,
  (state) => state.lastScrollToken
);

export const getIsLoading = createSelector(
  getCcfInteractionSearchState,
  (state) => state.isLoading
);

export const getCurrentSortColumn = createSelector(
  getCcfInteractionSearchState,
  (state) => state.currentSortColumn
);

export const getCustomizeMenuElement = createSelector(
  getCcfInteractionSearchState,
  (state) => state.customizeMenuElement
);

export const getFiltersMenuElement = createSelector(
  getCcfInteractionSearchState,
  (state) => state.filtersMenuElement
);

export const getNumberOfVisibleColumns = createSelector(
  getCcfInteractionSearchState,
  (state) => state.numberOfVisibleColumns
);

export const getSelectedRowContacts = createSelector(
  getCcfInteractionSearchState,
  (state) => state.selectedRowContacts
);

export const getSearchText = createSelector(
  getCcfInteractionSearchState,
  (state) => state.searchText
);

export const getDefaultFilterValues = createSelector(
  getCcfInteractionSearchState,
  (state) => state.defaultFilterValues
);

export const getAllChannelsData = createSelector(
  getCcfInteractionSearchState,
  (state) => state.allChannelList
);
export const getInteractionSearchToastObj = createSelector(
  getCcfInteractionSearchState,
  (state) => state.interactionSearchToastObj
);

export const getIsGridReset = createSelector(
  getCcfInteractionSearchState,
  (state) => state.isGridReset
);

export const getcurrentCustomerContactInfo = createSelector(
  getCcfInteractionSearchState,
  (state) => state.currentCustomerContactInfo
);

export const getIsFilterSelected = createSelector(
  getCcfInteractionSearchState,
  (state) => state.isFilterSelected
);

export const getSearchOption = createSelector(
  getCcfInteractionSearchState,
  (state) => state.searchOption
);